/**
 * Cross-chat analysis: detects patterns that span or compare multiple conversations.
 *
 * Exports:
 *   analyzeReinstruction(messages)     → ReinstructionAnalysis
 *   analyzeMessageGaps(messages)       → MessageGapAnalysis
 *   detectTopicRestarts(conversations) → TopicRestartAnalysis
 *   detectOrphanedChats(conversations) → OrphanedChatAnalysis
 *   analyzeCrossChat(conversations)    → CrossChatAnalysis
 *
 * @module cross-chat-analyzer
 */

/** @typedef {import('./types.js').FilteredMessage} FilteredMessage */
/** @typedef {import('./types.js').ConversationAnalysis} ConversationAnalysis */
/** @typedef {import('./types.js').ReinstructionAnalysis} ReinstructionAnalysis */
/** @typedef {import('./types.js').MessageGap} MessageGap */
/** @typedef {import('./types.js').MessageGapAnalysis} MessageGapAnalysis */
/** @typedef {import('./types.js').TopicRestartGroup} TopicRestartGroup */
/** @typedef {import('./types.js').TopicRestartAnalysis} TopicRestartAnalysis */
/** @typedef {import('./types.js').OrphanedChatAnalysis} OrphanedChatAnalysis */
/** @typedef {import('./types.js').CrossChatAnalysis} CrossChatAnalysis */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STOPWORDS = new Set([
  'the', 'be', 'to', 'of', 'and', 'a', 'in', 'that', 'have', 'i',
  'it', 'for', 'not', 'on', 'with', 'he', 'as', 'you', 'do', 'at',
  'this', 'but', 'his', 'by', 'from', 'they', 'we', 'say', 'her', 'she',
  'or', 'an', 'will', 'my', 'one', 'all', 'would', 'there', 'their', 'what',
  'so', 'up', 'out', 'if', 'about', 'who', 'get', 'which', 'go', 'me',
  'when', 'make', 'can', 'like', 'time', 'no', 'just', 'him', 'know', 'take',
  'people', 'into', 'year', 'your', 'good', 'some', 'could', 'them', 'see',
  'other', 'than', 'then', 'now', 'look', 'only', 'come', 'its', 'over',
  'think', 'also', 'back', 'after', 'use', 'two', 'how', 'our', 'work',
  'well', 'way', 'even', 'new', 'want', 'because', 'any', 'these', 'give',
]);

/** @type {{medium: number, long: number, stale: number}} */
const GAP_THRESHOLDS = {
  medium: 30 * 60,       // 30 minutes
  long: 2 * 60 * 60,     // 2 hours
  stale: 24 * 60 * 60,   // 24 hours
};

const REINSTRUCTION_THRESHOLD = 0.3;
const TITLE_SIMILARITY_THRESHOLD = 0.6;
const GENERIC_TITLES = new Set(['new chat', 'new conversation', '']);

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Extract significant words from text: lowercase, alpha-only, filter stopwords + short words.
 * @param {string} text
 * @returns {Set<string>}
 */
function extractSignificantWords(text) {
  const words = new Set();
  const tokens = text.toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/);
  for (const t of tokens) {
    if (t.length >= 4 && !STOPWORDS.has(t)) {
      words.add(t);
    }
  }
  return words;
}

/**
 * Jaccard similarity between two Sets.
 * @param {Set<string>} a
 * @param {Set<string>} b
 * @returns {number} 0-1
 */
function jaccardSimilarity(a, b) {
  if (a.size === 0 && b.size === 0) return 0;
  let intersection = 0;
  for (const w of a) {
    if (b.has(w)) intersection++;
  }
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

/**
 * Generate character bigrams from a string.
 * @param {string} s
 * @returns {Set<string>}
 */
function charBigrams(s) {
  const bigrams = new Set();
  const cleaned = s.toLowerCase().replace(/[^a-z0-9]/g, '');
  for (let i = 0; i < cleaned.length - 1; i++) {
    bigrams.add(cleaned.slice(i, i + 2));
  }
  return bigrams;
}

/**
 * Overlap coefficient on character bigrams: |intersection| / min(|a|, |b|).
 * @param {string} a
 * @param {string} b
 * @returns {number} 0-1
 */
function bigramOverlap(a, b) {
  const ba = charBigrams(a);
  const bb = charBigrams(b);
  if (ba.size === 0 || bb.size === 0) return 0;
  let intersection = 0;
  for (const bg of ba) {
    if (bb.has(bg)) intersection++;
  }
  return intersection / Math.min(ba.size, bb.size);
}

// ---------------------------------------------------------------------------
// Exported analysis functions
// ---------------------------------------------------------------------------

/**
 * Detect re-instruction patterns within a single conversation.
 * Flags user messages that repeat significant words from non-adjacent prior user messages.
 *
 * @param {FilteredMessage[]} messages
 * @returns {ReinstructionAnalysis}
 */
export function analyzeReinstruction(messages) {
  const userMessages = messages.filter((m) => m.role === 'user');
  const count = userMessages.length;

  if (count < 3) {
    return { reinstructionRate: 0, reinstructionCount: 0, userMessageCount: count };
  }

  // Pre-compute word sets
  const wordSets = userMessages.map((m) => extractSignificantWords(m.text));

  let flagged = 0;
  for (let i = 2; i < count; i++) {
    const current = wordSets[i];
    if (current.size === 0) continue;

    for (let j = 0; j < i - 1; j++) {
      if (wordSets[j].size === 0) continue;
      if (jaccardSimilarity(current, wordSets[j]) >= REINSTRUCTION_THRESHOLD) {
        flagged++;
        break; // only flag once per message
      }
    }
  }

  return {
    reinstructionRate: flagged / (count - 2),
    reinstructionCount: flagged,
    userMessageCount: count,
  };
}

/**
 * Find time gaps between consecutive messages indicating stale context returns.
 *
 * @param {FilteredMessage[]} messages
 * @returns {MessageGapAnalysis}
 */
export function analyzeMessageGaps(messages) {
  /** @type {MessageGap[]} */
  const gaps = [];
  let maxGapSeconds = 0;

  for (let i = 0; i < messages.length - 1; i++) {
    const prev = messages[i];
    const next = messages[i + 1];

    const prevEnd = prev.updateTime ?? prev.timestamp;
    const nextStart = next.timestamp;

    if (prevEnd == null || nextStart == null) continue;

    const gapSeconds = nextStart - prevEnd;
    if (gapSeconds < GAP_THRESHOLDS.medium) continue;

    /** @type {'medium'|'long'|'stale'} */
    let severity;
    if (gapSeconds >= GAP_THRESHOLDS.stale) {
      severity = 'stale';
    } else if (gapSeconds >= GAP_THRESHOLDS.long) {
      severity = 'long';
    } else {
      severity = 'medium';
    }

    gaps.push({ fromIndex: i, toIndex: i + 1, gapSeconds, severity });
    if (gapSeconds > maxGapSeconds) maxGapSeconds = gapSeconds;
  }

  return {
    gaps,
    staleReturns: gaps.filter((g) => g.severity === 'stale').length,
    longGaps: gaps.filter((g) => g.severity === 'long').length,
    mediumGaps: gaps.filter((g) => g.severity === 'medium').length,
    maxGapSeconds,
  };
}

/**
 * Detect conversations that are likely restarts of the same topic.
 * Two signals: shared gizmoId (strong) and title similarity (weak).
 *
 * @param {ConversationAnalysis[]} conversations
 * @returns {TopicRestartAnalysis}
 */
export function detectTopicRestarts(conversations) {
  /** @type {TopicRestartGroup[]} */
  const restartGroups = [];
  const gizmoGrouped = new Set(); // conversationIds already grouped by gizmo

  // 1. Strong signal: same gizmoId with 2+ conversations
  /** @type {Map<string, string[]>} */
  const gizmoMap = new Map();
  for (const conv of conversations) {
    if (conv.gizmoId) {
      const ids = gizmoMap.get(conv.gizmoId) || [];
      ids.push(conv.conversationId);
      gizmoMap.set(conv.gizmoId, ids);
    }
  }

  let gizmoRestartGroups = 0;
  for (const [gizmoId, ids] of gizmoMap) {
    if (ids.length >= 2) {
      restartGroups.push({
        signal: 'gizmo',
        key: gizmoId,
        conversationIds: ids,
        count: ids.length,
      });
      gizmoRestartGroups++;
      for (const id of ids) gizmoGrouped.add(id);
    }
  }

  // 2. Weak signal: title similarity among ungrouped conversations
  const ungrouped = conversations.filter(
    (c) => !gizmoGrouped.has(c.conversationId) && !GENERIC_TITLES.has((c.title || '').toLowerCase().trim())
  );

  const titleAssigned = new Set();
  let titleRestartGroups = 0;

  for (let i = 0; i < ungrouped.length; i++) {
    if (titleAssigned.has(ungrouped[i].conversationId)) continue;

    const group = [ungrouped[i].conversationId];

    for (let j = i + 1; j < ungrouped.length; j++) {
      if (titleAssigned.has(ungrouped[j].conversationId)) continue;

      if (bigramOverlap(ungrouped[i].title, ungrouped[j].title) >= TITLE_SIMILARITY_THRESHOLD) {
        group.push(ungrouped[j].conversationId);
      }
    }

    if (group.length >= 2) {
      restartGroups.push({
        signal: 'title',
        key: ungrouped[i].title,
        conversationIds: group,
        count: group.length,
      });
      titleRestartGroups++;
      for (const id of group) titleAssigned.add(id);
    }
  }

  // Sort by group size descending
  restartGroups.sort((a, b) => b.count - a.count);

  let totalRestartConversations = 0;
  for (const g of restartGroups) totalRestartConversations += g.count;

  return {
    restartGroups,
    totalRestartGroups: restartGroups.length,
    totalRestartConversations,
    gizmoRestartGroups,
    titleRestartGroups,
  };
}

/**
 * Find abandoned single/two-exchange conversations.
 *
 * @param {ConversationAnalysis[]} conversations
 * @returns {OrphanedChatAnalysis}
 */
export function detectOrphanedChats(conversations) {
  /** @type {string[]} */
  const orphanedIds = [];
  let singleExchange = 0;
  let abandonedStart = 0;

  // Build gizmo usage map (gizmoId → count of conversations with 3+ messages)
  const gizmoLongConvs = new Set();
  for (const conv of conversations) {
    if (conv.gizmoId && conv.messageCount > 2) {
      gizmoLongConvs.add(conv.gizmoId);
    }
  }

  for (const conv of conversations) {
    if (conv.messageCount === 0 || conv.messageCount > 2) continue;

    orphanedIds.push(conv.conversationId);

    if (conv.userMessageCount === 1 && conv.assistantMessageCount === 1) {
      singleExchange++;
    } else if (conv.userMessageCount === 1 && conv.assistantMessageCount === 0) {
      abandonedStart++;
    }
  }

  // Count orphans that share a gizmoId with longer conversations
  let orphanedWithGizmo = 0;
  for (const conv of conversations) {
    if (conv.messageCount >= 1 && conv.messageCount <= 2 && conv.gizmoId && gizmoLongConvs.has(conv.gizmoId)) {
      orphanedWithGizmo++;
    }
  }

  return {
    orphanedIds,
    orphanedCount: orphanedIds.length,
    singleExchange,
    abandonedStart,
    orphanedWithGizmo,
  };
}

/**
 * Detect user messages that received no echo in subsequent assistant responses.
 * Uses overlap coefficient (intersection / |userWords|) with a low threshold
 * to catch messages where the model showed any sign of processing.
 *
 * @param {FilteredMessage[]} messages
 * @returns {import('./types.js').ContextWasteAnalysis}
 */
export function analyzeContextWaste(messages) {
  const userMessages = [];
  const assistantMessages = [];

  for (let i = 0; i < messages.length; i++) {
    if (messages[i].role === 'user') {
      userMessages.push({ index: i, text: messages[i].text });
    } else if (messages[i].role === 'assistant') {
      assistantMessages.push({ index: i, text: messages[i].text });
    }
  }

  const userCount = userMessages.length;
  if (userCount < 2) {
    return { wastedMessageCount: 0, wastedMessageIndices: [], contextWasteRate: 0, userMessageCount: userCount };
  }

  /** @type {number[]} */
  const wastedIndices = [];
  const ECHO_THRESHOLD = 0.15;

  for (const um of userMessages) {
    const userWords = extractSignificantWords(um.text);
    if (userWords.size === 0) continue;

    let echoFound = false;
    for (const am of assistantMessages) {
      if (am.index <= um.index) continue; // only check subsequent responses
      const responseWords = extractSignificantWords(am.text);
      let intersection = 0;
      for (const w of userWords) {
        if (responseWords.has(w)) intersection++;
      }
      const overlap = intersection / userWords.size;
      if (overlap >= ECHO_THRESHOLD) {
        echoFound = true;
        break;
      }
    }

    if (!echoFound) {
      wastedIndices.push(um.index);
    }
  }

  return {
    wastedMessageCount: wastedIndices.length,
    wastedMessageIndices: wastedIndices,
    contextWasteRate: userCount > 0 ? wastedIndices.length / userCount : 0,
    userMessageCount: userCount,
  };
}

/**
 * Top-level cross-chat aggregator.
 * Runs per-conversation analyses (reinstruction, gaps) and cross-conversation analyses
 * (topic restarts, orphaned chats). Mutates each ConversationAnalysis to attach results.
 *
 * @param {ConversationAnalysis[]} conversations
 * @returns {CrossChatAnalysis}
 */
export function analyzeCrossChat(conversations) {
  let reinstructionSum = 0;
  let reinstructionEligible = 0;
  let totalStaleReturns = 0;
  let totalLongGaps = 0;

  let totalWastedMessages = 0;
  let wasteEligible = 0;
  let wasteRateSum = 0;

  for (const conv of conversations) {
    // Per-conversation: reinstruction
    const reinstruction = analyzeReinstruction(conv.messages);
    conv.reinstruction = reinstruction;

    if (reinstruction.userMessageCount >= 3) {
      reinstructionSum += reinstruction.reinstructionRate;
      reinstructionEligible++;
    }

    // Per-conversation: message gaps
    const messageGaps = analyzeMessageGaps(conv.messages);
    conv.messageGaps = messageGaps;

    totalStaleReturns += messageGaps.staleReturns;
    totalLongGaps += messageGaps.longGaps;

    // Per-conversation: context waste
    const contextWaste = analyzeContextWaste(conv.messages);
    conv.contextWaste = contextWaste;

    totalWastedMessages += contextWaste.wastedMessageCount;
    if (contextWaste.userMessageCount >= 2) {
      wasteRateSum += contextWaste.contextWasteRate;
      wasteEligible++;
    }
  }

  // Cross-conversation analyses
  const topicRestarts = detectTopicRestarts(conversations);
  const orphanedChats = detectOrphanedChats(conversations);

  return {
    topicRestarts,
    orphanedChats,
    avgReinstructionRate: reinstructionEligible > 0 ? reinstructionSum / reinstructionEligible : 0,
    totalStaleReturns,
    totalLongGaps,
    totalWastedMessages,
    avgContextWasteRate: wasteEligible > 0 ? wasteRateSum / wasteEligible : 0,
  };
}
